import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

// window.confirm()/window.alert()는 브라우저 기본 팝업이라 폰트·버튼·색이
// 앱 전체 스타일(Tailwind로 다듬어 놓은 것)과 완전히 따로 논다. 이
// 컨텍스트는 둘 다 대체한다:
//   const confirm = useConfirm(); if (!(await confirm('...'))) return;
//   const notify = useAlert();   await notify('...');
// 둘 다 async라 await가 필요하다는 점만 window.confirm/alert와 다르고,
// 나머지 호출부 흐름은 거의 그대로 옮길 수 있다.
const DialogContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  // mode: 'confirm'(취소/확인 두 버튼, boolean 반환) | 'alert'(확인 한
  // 버튼만, 항상 true 반환) - 같은 모달 UI를 재사용하고 버튼 구성만 다르다.
  const [dialog, setDialog] = useState(null);
  // resolve 콜백을 state가 아니라 ref로 들고 있는다 - 함수를 state에
  // 그대로 넣으면 React가 "updater 함수"로 오해해서 즉시 호출해버린다.
  const resolveRef = useRef(null);

  const openDialog = useCallback((message, mode) => {
    setDialog({ message, mode });
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const confirm = useCallback((message) => openDialog(message, 'confirm'), [openDialog]);
  const notify = useCallback((message) => openDialog(message, 'alert'), [openDialog]);

  const handleChoice = (result) => {
    setDialog(null);
    resolveRef.current && resolveRef.current(result);
    resolveRef.current = null;
  };

  const isConfirm = dialog?.mode === 'confirm';

  // 모달이 떠 있는 동안 뒤에 깔린 화면이 같이 스크롤되던 문제 -
  // MyPage와 같은 패턴(열려있는 동안만 막고, 닫히면 원래 값으로 복원).
  useEffect(() => {
    if (!dialog) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [dialog]);

  return (
    <DialogContext.Provider value={{ confirm, notify }}>
      {children}
      {dialog && (
        <div
          data-confirm-dialog
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-start gap-3">
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  isConfirm ? 'bg-amber-50' : 'bg-blue-50'
                }`}
              >
                {isConfirm ? (
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                ) : (
                  <Info className="w-5 h-5 text-blue-500" />
                )}
              </div>
              <p className="text-gray-800 text-sm leading-relaxed pt-2 whitespace-pre-line">{dialog.message}</p>
            </div>
            <div className="flex gap-3 mt-6">
              {isConfirm && (
                <button
                  type="button"
                  onClick={() => handleChoice(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-md hover:bg-gray-200 font-medium transition-colors"
                >
                  취소
                </button>
              )}
              <button
                type="button"
                onClick={() => handleChoice(true)}
                className="flex-1 bg-blue-500 text-white py-2.5 rounded-md hover:bg-blue-600 font-medium transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};

const useDialogContext = (hookName) => {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error(`${hookName}은 ConfirmProvider 안에서만 쓸 수 있습니다.`);
  }
  return ctx;
};

// useConfirm()은 async 함수 하나를 돌려준다: await confirm('메시지')로
// 쓰면 취소/확인 모달이 뜨고, 사용자의 선택(boolean)이 그대로 await의
// 반환값이 된다 - window.confirm()과 호출부 사용법이 거의 같다.
export const useConfirm = () => useDialogContext('useConfirm').confirm;

// useAlert()는 확인 버튼 하나만 있는 안내 모달을 띄운다. window.alert()
// 대체용 - await notify('메시지')로 쓰고, 사용자가 확인을 누를 때까지
// 기다린 뒤 다음 코드로 넘어간다(반환값 자체는 항상 true라 보통은 무시).
export const useAlert = () => useDialogContext('useAlert').notify;
